import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  MessageCircle,
  Mail,
  Phone,
  ChevronRight,
  BookOpen,
  Paperclip,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";

const faqs = [
  {
    q: "How do I create a new job posting?",
    a: "Navigate to Jobs > Create Job and fill in the required fields.",
  },
  {
    q: "How do referral campaigns work?",
    a: "Set up a campaign with a budget and share the referral link with your network.",
  },
  {
    q: "Can I edit a closed job?",
    a: "Yes, reopen and edit any closed job from the Jobs page.",
  },
  {
    q: "How to export applications?",
    a: "Go to Job Record and click Export to download CSV.",
  },
  {
    q: "How do I change notification settings?",
    a: "Go to Settings > Alerts to toggle each notification type.",
  },
  {
    q: "Can I integrate with Slack?",
    a: "Yes, enable Slack notifications from Settings > Alerts.",
  },
];

const ticketStatusColors: Record<string, string> = {
  Open: "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  "In Progress":
    "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  Resolved:
    "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
};
const priorityColors: Record<string, string> = {
  High: "text-destructive bg-destructive/8",
  Medium: "text-warning bg-[hsl(var(--warning)/0.08)]",
  Low: "text-muted-foreground bg-muted",
};

const Support = () => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const [tickets, setTickets] = useState([
    {
      id: "TKT-001",
      subject: "Unable to post on LinkedIn",
      status: "Open",
      priority: "High",
      category: "Integration",
      date: "12 Mar",
      assignee: "Support Team",
    },
    {
      id: "TKT-002",
      subject: "Resume download not working",
      status: "In Progress",
      priority: "Medium",
      category: "Bug",
      date: "10 Mar",
      assignee: "Dev Team",
    },
    {
      id: "TKT-003",
      subject: "Duplicate applications appearing",
      status: "Resolved",
      priority: "Low",
      category: "Bug",
      date: "8 Mar",
      assignee: "Support Team",
    },
    {
      id: "TKT-004",
      subject: "Referral link expired prematurely",
      status: "Open",
      priority: "High",
      category: "Feature",
      date: "7 Mar",
      assignee: "Product Team",
    },
    {
      id: "TKT-005",
      subject: "Email notifications delayed",
      status: "In Progress",
      priority: "Low",
      category: "Infrastructure",
      date: "3 Mar",
      assignee: "Dev Team",
    },
  ]);

  const handleSubmitTicket = () => {
    if (!subject.trim() || !description.trim() || !category) {
      toast({
        title: "Missing fields",
        description: "Please fill Subject, Category and Description.",
        variant: "destructive",
      });
      return;
    }
    const newTicket = {
      id: `TKT-${String(tickets.length + 1).padStart(3, "0")}`,
      subject: subject.trim(),
      status: "Open",
      priority: priority || "Medium",
      category:
        category === "bug"
          ? "Bug"
          : category === "feature"
            ? "Feature"
            : category === "integration"
              ? "Integration"
              : category === "account"
                ? "Account"
                : "Other",
      date: new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      }),
      assignee: "Support Team",
    };
    setTickets(prev => [newTicket, ...prev]);
    setSubject("");
    setDescription("");
    setCategory("");
    setPriority("");
    toast({
      title: "Ticket Submitted",
      description: `${newTicket.id} created. We'll respond within 24 hours.`,
    });
  };

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={item}>
          <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
            Help & Support
          </h1>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Get help or contact our team
          </p>
        </motion.div>

        <motion.div variants={item} className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            {
              icon: Mail,
              title: "Email",
              desc: "support@findjobs.com",
              color: "text-primary",
              bg: "bg-primary/8",
              action: () => {
                window.location.href = "mailto:support@findjobs.com";
              },
            },
            {
              icon: MessageCircle,
              title: "Live Chat",
              desc: "Mon–Fri, 9 AM – 6 PM",
              color: "text-success",
              bg: "bg-[hsl(var(--status-active)/0.08)]",
              action: () =>
                toast({
                  title: "Live Chat",
                  description: "Chat is available Mon–Fri, 9 AM – 6 PM IST.",
                }),
            },
            {
              icon: Phone,
              title: "Phone",
              desc: "+91 80 4567 8900",
              color: "text-warning",
              bg: "bg-[hsl(var(--warning)/0.08)]",
              action: () => {
                window.location.href = "tel:+918045678900";
              },
            },
          ].map(c => (
            <div
              key={c.title}
              onClick={c.action}
              className="section-card flex cursor-pointer flex-col items-center gap-2 p-3 text-center transition-all hover:border-primary/15 sm:flex-row sm:items-center sm:gap-3 sm:text-left"
            >
              <div
                className={`h-9 w-9 rounded-xl ${c.bg} flex shrink-0 items-center justify-center`}
              >
                <c.icon className={`h-4 w-4 ${c.color}`} />
              </div>
              <div className="min-w-0">
                <p className="text-[12px] font-semibold text-foreground">
                  {c.title}
                </p>
                <p className="hidden truncate text-[10px] text-muted-foreground sm:block">
                  {c.desc}
                </p>
              </div>
            </div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
          <motion.div
            variants={item}
            className="section-card overflow-hidden lg:col-span-2"
          >
            <div className="border-b border-border/50 px-4 pb-3 pt-4">
              <h2 className="font-display text-[13px] font-semibold text-foreground">
                Submit a Ticket
              </h2>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                We'll respond within 24 hours
              </p>
            </div>
            <div className="space-y-3 p-4">
              <div className="space-y-1">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Subject <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="Brief description of your issue"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="h-9 rounded-lg bg-background text-[12px]"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Category <span className="text-destructive">*</span>
                  </label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="h-9 rounded-lg bg-background text-[12px]">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">Bug / Error</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="integration">
                        Integration Issue
                      </SelectItem>
                      <SelectItem value="account">Account & Billing</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Priority
                  </label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="h-9 rounded-lg bg-background text-[12px]">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Description <span className="text-destructive">*</span>
                </label>
                <Textarea
                  placeholder="Steps to reproduce, expected vs actual behavior..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="min-h-[80px] rounded-lg bg-background text-[12px]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Attachment
                </label>
                <div className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/20 p-2.5 transition-colors hover:bg-muted/30">
                  <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] text-muted-foreground">
                    Drag & drop or click to attach screenshot
                  </span>
                </div>
              </div>
              <Button
                className="btn-glow h-8 w-full rounded-lg text-[12px] font-semibold"
                onClick={handleSubmitTicket}
              >
                Submit Ticket
              </Button>
            </div>
          </motion.div>

          <motion.div
            variants={item}
            className="section-card overflow-hidden lg:col-span-3"
          >
            <div className="border-b border-border/50 px-4 pb-3 pt-4">
              <h2 className="font-display text-[13px] font-semibold text-foreground">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="divide-y divide-border/30">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="group cursor-pointer px-4 py-3 transition-colors hover:bg-accent/15"
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                >
                  <div className="flex items-start gap-2">
                    <BookOpen className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                    <div className="flex-1">
                      <p className="text-[12px] font-semibold text-foreground">
                        {faq.q}
                      </p>
                      <p
                        className={`mt-0.5 text-[11px] text-muted-foreground ${expandedFaq === i ? "" : "line-clamp-1"}`}
                      >
                        {faq.a}
                      </p>
                    </div>
                    <ChevronRight
                      className={`mt-0.5 h-3 w-3 shrink-0 text-muted-foreground transition-all group-hover:text-primary ${expandedFaq === i ? "rotate-90" : ""}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div variants={item}>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-display text-[13px] font-semibold text-foreground">
              Recent Tickets
            </h2>
          </div>

          <div className="space-y-2 sm:hidden">
            {tickets.map(t => (
              <div key={t.id} className="section-card p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-mono text-[10px] font-semibold text-primary">
                    {t.id}
                  </span>
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[9px] font-semibold ${ticketStatusColors[t.status]}`}
                  >
                    {t.status}
                  </span>
                </div>
                <p className="text-[12px] font-medium text-foreground">
                  {t.subject}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[9px] font-semibold ${priorityColors[t.priority]}`}
                  >
                    {t.priority}
                  </span>
                  <span className="rounded-md bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    {t.category}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {t.date}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="section-card hidden overflow-hidden sm:block">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border/50 bg-muted/30 hover:bg-muted/30">
                    {[
                      "Ticket",
                      "Subject",
                      "Category",
                      "Priority",
                      "Status",
                      "Assignee",
                      "Date",
                    ].map(h => (
                      <TableHead
                        key={h}
                        className="py-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                      >
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map(t => (
                    <TableRow
                      key={t.id}
                      className="table-row-hover border-b border-border/30 last:border-0"
                    >
                      <TableCell className="py-3 font-mono text-[11px] font-semibold text-primary">
                        {t.id}
                      </TableCell>
                      <TableCell className="py-3 text-[12px] font-medium text-foreground">
                        {t.subject}
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                          {t.category}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold ${priorityColors[t.priority]}`}
                        >
                          {t.priority}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold ${ticketStatusColors[t.status]}`}
                        >
                          {t.status}
                        </span>
                      </TableCell>
                      <TableCell className="py-3 text-[12px] text-muted-foreground">
                        {t.assignee}
                      </TableCell>
                      <TableCell className="py-3 text-[12px] text-muted-foreground">
                        {t.date}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
};

export default Support;
